import React, { useState } from 'react';
import { Form, Input, Upload, Button, message, Select, DatePicker, Radio, TimePicker } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { db, storage, complaints } from '../firebase/firebase';
import { addDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import logo from '../assets/logo.png';
import GooglePlacesAutocomplete from 'react-google-places-autocomplete';

const { Option } = Select;

const ComplaintForm = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    const [pickUpLocation, setPickUpLocation] = useState(null);
    const [dropOffLocation, setDropOffLocation] = useState(null);

    const generateComplaintId = () => {
        return Math.floor(1000000000 + Math.random() * 9000000000).toString(); // Generates a 10-digit random number
    };



    const onFinish = async (values) => {
        if (!pickUpLocation || !dropOffLocation) {
            message.error("Please select both pickup and drop-off locations");
            return;
        }

        setLoading(true);
        try {
            // Handle file uploads
            const uploadFile = async (file) => {
                if (!file) return null;
                const storageRef = ref(storage, `complaints/${file.name}`);
                await uploadBytes(storageRef, file.originFileObj);
                return getDownloadURL(storageRef);
            };

            const files1Urls = await Promise.all((values.files1 || []).map(uploadFile));
            const files2Urls = await Promise.all((values.files2 || []).map(uploadFile));

            // Prepare data for Firestore
            const complaintData = {
                complaintId: generateComplaintId(),
                name: values.name || '',
                phone: values.phone || '',
                email: values.email || '',
                driverName: values.driverName || '',
                carNumber: values.carNumber || '',
                meterId: values.meterId || '',
                queryRegarding: values.queryRegarding || '',
                dateOfTravel: values.dateOfTravel ? values.dateOfTravel.toDate() : null,
                timeOfTravel: values.timeOfTravel ? values.timeOfTravel.format('HH:mm') : null,
                exactAmountCharged: values.exactAmountCharged || '',
                pickUpLocation: pickUpLocation.label,
                dropOffLocation: dropOffLocation.label,
                receiptType: values.receiptType || '',
                files1: files1Urls.filter(url => url !== null),
                files2: files2Urls.filter(url => url !== null),
                additionalComments: values.additionalComments || '',
                createdAt: new Date(),
                status: 'Pending',  // Set initial status to 'Pending'
                isRead: false,  // Set initial status to 'Pending'
            };


            await addDoc(complaints, complaintData);

            message.success('Complaint submitted successfully');
            form.resetFields();
            setPickUpLocation(null);
            setDropOffLocation(null);
        } catch (error) {
            console.error('Error submitting complaint:', error);
            message.error('Failed to submit complaint');
        }
        setLoading(false);
    };

    const normFile = (e) => {
        if (Array.isArray(e)) {
            return e;
        }
        return e?.fileList;
    };

    return (
        <div style={{ textAlign: 'center', padding: '40px', backgroundColor: '#f0f2f5' }}>
            <img src={logo} alt="Company Logo" style={{ width: '150px', marginBottom: '20px' }} />
            <h1 style={{ color: '#1890ff', marginBottom: '20px' }}>Complaint Form</h1>
            <Form
                form={form}
                name="complaint_form"
                onFinish={onFinish}
                layout="vertical"
                style={{ maxWidth: 600, margin: '0 auto', padding: '30px', border: '1px solid #d9d9d9', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', backgroundColor: '#ffffff' }}
            >
                <h2 style={{ color: '#ff4d4f', marginBottom: '20px' }}>Step 1: Your Contact Details</h2>
                <p>So we can contact you regarding your enquiry.</p>
                <Form.Item
                    name="name"
                    rules={[{ required: true, message: 'Please input your name' }]}
                >
                    <Input placeholder="Name" style={{ borderRadius: '4px' }} />
                </Form.Item>
                <Form.Item
                    name="phone"
                    rules={[
                        { required: true, message: 'Please input your phone number' },
                        { pattern: /^(?:\+?61|0)[2-478](\s?\d){8}$/, message: 'Please input a valid Australian phone number' }
                    ]}
                >
                    <Input placeholder="Phone Number" style={{ borderRadius: '4px' }} />
                </Form.Item>
                <Form.Item
                    name="email"
                    rules={[
                        { required: true, message: 'Please input your email' },
                        { type: 'email', message: 'Please enter a valid email' },
                    ]}
                >
                    <Input placeholder="Email" style={{ borderRadius: '4px' }} />
                </Form.Item>

                <h2 style={{ color: '#ff4d4f', marginBottom: '20px' }}>Step 2: Driver  Contact Details</h2>
                <p>So we can contact you regarding your enquiry.</p>
                <Form.Item name="driverName">
                    <Input placeholder="Driver Name" />
                </Form.Item>
                <Form.Item name="carNumber">
                    <Input placeholder="Car Number" />
                </Form.Item>
                <Form.Item name="meterId">
                    <Input placeholder="Meter ID" />
                </Form.Item>


                <h2 style={{ color: '#ff4d4f', marginTop: '40px', marginBottom: '20px' }}>Step 3: How Can We Help?</h2>
                <p>Tell us about your enquiry.</p>
                <Form.Item
                    name="queryRegarding"
                    rules={[{ required: true, message: 'Please select what your query is regarding' }]}
                >
                    <Select placeholder="What is your query regarding?">
                        <Option value="overcharged">I travelled in a taxi but believe I have been overcharged</Option>
                        <Option value="lostItem">I left an item in a taxi</Option>
                        <Option value="driverComplaint">I want to make a complaint about a driver</Option>
                        <Option value="other">Other</Option>
                    </Select>
                </Form.Item>
                <Form.Item
                    name="dateOfTravel"
                    rules={[{ required: true, message: 'Please select the date of travel' }]}
                >
                    <DatePicker
                        style={{ width: '100%' }}
                        placeholder="Date of travel (or charge)"
                        disabledDate={(current) => current && current > Date.now()} // Disable future dates
                    />
                </Form.Item>
                <Form.Item
                    name="timeOfTravel"
                    rules={[{ required: true, message: 'Please select the time of travel' }]}
                >
                    <TimePicker style={{ width: '100%' }} format="HH:mm" placeholder="Time of travel" />
                </Form.Item>
                <Form.Item
                    name="exactAmountCharged"
                    rules={[
                        { required: true, message: 'Please input the exact amount charged' },
                        {
                            validator: (_, value) => {
                                if (!value || isNaN(value)) {
                                    return Promise.reject(new Error('Amount must be a number'));
                                }
                                return Promise.resolve();
                            },
                        },
                    ]}
                >
                    <Input
                        placeholder="Exact amount charged"
                        style={{ borderRadius: '4px' }}
                        type="number"  // Ensure numeric input
                    />
                </Form.Item>




                <Form.Item label="Pick Up Location" rules={[{ required: true, message: 'Please select the pickup location' }]}>
                    <GooglePlacesAutocomplete
                        apiKey="AIzaSyBFx-2FDM-5VnMNeCXRDA_cflnenwkzXkc"
                        selectProps={{
                            value: pickUpLocation,
                            onChange: setPickUpLocation,
                            placeholder: 'Enter pickup location',
                        }}
                    />
                </Form.Item>

                <Form.Item label="Drop Off Location" rules={[{ required: true, message: 'Please select the drop-off location' }]}>
                    <GooglePlacesAutocomplete
                        apiKey="AIzaSyBFx-2FDM-5VnMNeCXRDA_cflnenwkzXkc"
                        selectProps={{
                            value: dropOffLocation,
                            onChange: setDropOffLocation,
                            placeholder: 'Enter drop-off location',
                        }}
                    />
                </Form.Item>




                <h2 style={{ color: '#ff4d4f', marginTop: '40px', marginBottom: '20px' }}>Step 4: Additional Details</h2>
                <p>Provide as much detail as possible to help us action your enquiry.</p>
                <Form.Item
                    name="receiptType"
                    rules={[{ required: true, message: 'Please select one of the following' }]}
                >
                    <Radio.Group>
                        <Radio value="harbourCabs">I have a receipt from Harbour Cabs</Radio>
                        <Radio value="creditCard">I have a charge on my credit card statement from Harbour Cabs</Radio>
                        <Radio value="otherCompany">I have been charged by another company but the taxi I travelled in was a Harbour Cabs vehicle and I have the taxi number</Radio>
                    </Radio.Group>
                </Form.Item>

                {/* Required image */}
                <Form.Item name="files1" valuePropName="fileList" rules={[{ required: true, message: 'Please upload at least one image' }]} getValueFromEvent={normFile}>
                    <Upload beforeUpload={() => false} listType="picture" accept=".jpg,.png">
                        <Button icon={<UploadOutlined />}>Upload Required Image</Button>
                    </Upload>
                </Form.Item>
                {/* Optional image */}
                <Form.Item name="files2" valuePropName="fileList" getValueFromEvent={normFile}>
                    <Upload beforeUpload={() => false} listType="picture" accept=".jpg,.png">
                        <Button icon={<UploadOutlined />}>Upload Optional Image</Button>
                    </Upload>
                </Form.Item>

                <p>JPG or PNG images only</p>

                <Form.Item
                    rules={[{ required: true, message: 'Please input the reason' }]}
                    name="additionalComments"
                >
                    <Input.TextArea rows={4} placeholder="Additional Comments" style={{ borderRadius: '4px' }} />
                </Form.Item>

                <Form.Item>
                    <Button type="primary" htmlType="submit" loading={loading} style={{ backgroundColor: '#ff4d4f', borderColor: '#ff4d4f', width: '100%', borderRadius: '4px' }}>
                        Submit
                    </Button>
                </Form.Item>
            </Form>
        </div>
    );
};

export default ComplaintForm;